import React, { useState, useEffect, useRef } from 'react';
import {
  X, Calendar, Clock, User, Mail, Building, Globe,
  ChevronDown, ArrowRight, ArrowLeft, Check, MapPin, ExternalLink, AlertCircle, Loader2, CalendarX
} from 'lucide-react';
import { trackDemoBooked } from '../../lib/track';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string) || 'https://utimppxbdeptpoyotmcx.supabase.co';
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InV0aW1wcHhiZGVwdHBveW90bWN4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjUyODE2MzIsImV4cCI6MjA4MDg1NzYzMn0.qqA1gLxfXiJmGmcIIhm6RBTaBVAhvRSgYF97T42TF3s';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Country {
  name: string;
  code: string;
  dial: string;
  timezone: string;
  flag: string;
}

interface TimezoneOption {
  label: string;
  tz: string;
}

interface Slot {
  start: string;
  end: string;
}

interface SlotDay {
  date: string;
  slots: Slot[];
}

interface SlotsResponse {
  days: SlotDay[];
  timezone: string;
  lead_timezone: string;
  sales_member_id: string;
  sales_member_name: string;
}

interface BookingResult {
  success: boolean;
  meeting_id: string;
  lead_id: string;
  meeting_link: string;
  meeting_date: string;
  meeting_time: string;
  timezone: string;
}

// ── Flag component ─────────────────────────────────────────────────────────────

function FlagImg({ flag }: { flag: string }) {
  return (
    <span
      style={{
        fontFamily: '"Noto Color Emoji", "Apple Color Emoji", "Segoe UI Emoji", "Twemoji Mozilla", "Android Emoji", sans-serif',
        fontSize: '16px',
        lineHeight: 1,
        display: 'inline-block',
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      {flag}
    </span>
  );
}

// ── Static data ────────────────────────────────────────────────────────────────

const COUNTRIES: Country[] = [
  { name: 'Afghanistan', code: 'AF', dial: '+93', timezone: 'Asia/Kabul', flag: '🇦🇫' },
  { name: 'Algeria', code: 'DZ', dial: '+213', timezone: 'Africa/Algiers', flag: '🇩🇿' },
  { name: 'Argentina', code: 'AR', dial: '+54', timezone: 'America/Argentina/Buenos_Aires', flag: '🇦🇷' },
  { name: 'Australia', code: 'AU', dial: '+61', timezone: 'Australia/Sydney', flag: '🇦🇺' },
  { name: 'Austria', code: 'AT', dial: '+43', timezone: 'Europe/Vienna', flag: '🇦🇹' },
  { name: 'Bahrain', code: 'BH', dial: '+973', timezone: 'Asia/Bahrain', flag: '🇧🇭' },
  { name: 'Bangladesh', code: 'BD', dial: '+880', timezone: 'Asia/Dhaka', flag: '🇧🇩' },
  { name: 'Belgium', code: 'BE', dial: '+32', timezone: 'Europe/Brussels', flag: '🇧🇪' },
  { name: 'Brazil', code: 'BR', dial: '+55', timezone: 'America/Sao_Paulo', flag: '🇧🇷' },
  { name: 'Canada', code: 'CA', dial: '+1', timezone: 'America/Toronto', flag: '🇨🇦' },
  { name: 'Chile', code: 'CL', dial: '+56', timezone: 'America/Santiago', flag: '🇨🇱' },
  { name: 'China', code: 'CN', dial: '+86', timezone: 'Asia/Shanghai', flag: '🇨🇳' },
  { name: 'Colombia', code: 'CO', dial: '+57', timezone: 'America/Bogota', flag: '🇨🇴' },
  { name: 'Croatia', code: 'HR', dial: '+385', timezone: 'Europe/Zagreb', flag: '🇭🇷' },
  { name: 'Czech Republic', code: 'CZ', dial: '+420', timezone: 'Europe/Prague', flag: '🇨🇿' },
  { name: 'Denmark', code: 'DK', dial: '+45', timezone: 'Europe/Copenhagen', flag: '🇩🇰' },
  { name: 'Egypt', code: 'EG', dial: '+20', timezone: 'Africa/Cairo', flag: '🇪🇬' },
  { name: 'Ethiopia', code: 'ET', dial: '+251', timezone: 'Africa/Addis_Ababa', flag: '🇪🇹' },
  { name: 'Finland', code: 'FI', dial: '+358', timezone: 'Europe/Helsinki', flag: '🇫🇮' },
  { name: 'France', code: 'FR', dial: '+33', timezone: 'Europe/Paris', flag: '🇫🇷' },
  { name: 'Germany', code: 'DE', dial: '+49', timezone: 'Europe/Berlin', flag: '🇩🇪' },
  { name: 'Ghana', code: 'GH', dial: '+233', timezone: 'Africa/Accra', flag: '🇬🇭' },
  { name: 'Greece', code: 'GR', dial: '+30', timezone: 'Europe/Athens', flag: '🇬🇷' },
  { name: 'Hong Kong', code: 'HK', dial: '+852', timezone: 'Asia/Hong_Kong', flag: '🇭🇰' },
  { name: 'Hungary', code: 'HU', dial: '+36', timezone: 'Europe/Budapest', flag: '🇭🇺' },
  { name: 'India', code: 'IN', dial: '+91', timezone: 'Asia/Kolkata', flag: '🇮🇳' },
  { name: 'Indonesia', code: 'ID', dial: '+62', timezone: 'Asia/Jakarta', flag: '🇮🇩' },
  { name: 'Iran', code: 'IR', dial: '+98', timezone: 'Asia/Tehran', flag: '🇮🇷' },
  { name: 'Iraq', code: 'IQ', dial: '+964', timezone: 'Asia/Baghdad', flag: '🇮🇶' },
  { name: 'Ireland', code: 'IE', dial: '+353', timezone: 'Europe/Dublin', flag: '🇮🇪' },
  { name: 'Israel', code: 'IL', dial: '+972', timezone: 'Asia/Jerusalem', flag: '🇮🇱' },
  { name: 'Italy', code: 'IT', dial: '+39', timezone: 'Europe/Rome', flag: '🇮🇹' },
  { name: 'Japan', code: 'JP', dial: '+81', timezone: 'Asia/Tokyo', flag: '🇯🇵' },
  { name: 'Jordan', code: 'JO', dial: '+962', timezone: 'Asia/Amman', flag: '🇯🇴' },
  { name: 'Kenya', code: 'KE', dial: '+254', timezone: 'Africa/Nairobi', flag: '🇰🇪' },
  { name: 'Kuwait', code: 'KW', dial: '+965', timezone: 'Asia/Kuwait', flag: '🇰🇼' },
  { name: 'Lebanon', code: 'LB', dial: '+961', timezone: 'Asia/Beirut', flag: '🇱🇧' },
  { name: 'Malaysia', code: 'MY', dial: '+60', timezone: 'Asia/Kuala_Lumpur', flag: '🇲🇾' },
  { name: 'Mexico', code: 'MX', dial: '+52', timezone: 'America/Mexico_City', flag: '🇲🇽' },
  { name: 'Morocco', code: 'MA', dial: '+212', timezone: 'Africa/Casablanca', flag: '🇲🇦' },
  { name: 'Netherlands', code: 'NL', dial: '+31', timezone: 'Europe/Amsterdam', flag: '🇳🇱' },
  { name: 'New Zealand', code: 'NZ', dial: '+64', timezone: 'Pacific/Auckland', flag: '🇳🇿' },
  { name: 'Nigeria', code: 'NG', dial: '+234', timezone: 'Africa/Lagos', flag: '🇳🇬' },
  { name: 'Norway', code: 'NO', dial: '+47', timezone: 'Europe/Oslo', flag: '🇳🇴' },
  { name: 'Oman', code: 'OM', dial: '+968', timezone: 'Asia/Muscat', flag: '🇴🇲' },
  { name: 'Pakistan', code: 'PK', dial: '+92', timezone: 'Asia/Karachi', flag: '🇵🇰' },
  { name: 'Peru', code: 'PE', dial: '+51', timezone: 'America/Lima', flag: '🇵🇪' },
  { name: 'Philippines', code: 'PH', dial: '+63', timezone: 'Asia/Manila', flag: '🇵🇭' },
  { name: 'Poland', code: 'PL', dial: '+48', timezone: 'Europe/Warsaw', flag: '🇵🇱' },
  { name: 'Portugal', code: 'PT', dial: '+351', timezone: 'Europe/Lisbon', flag: '🇵🇹' },
  { name: 'Qatar', code: 'QA', dial: '+974', timezone: 'Asia/Qatar', flag: '🇶🇦' },
  { name: 'Romania', code: 'RO', dial: '+40', timezone: 'Europe/Bucharest', flag: '🇷🇴' },
  { name: 'Russia', code: 'RU', dial: '+7', timezone: 'Europe/Moscow', flag: '🇷🇺' },
  { name: 'Saudi Arabia', code: 'SA', dial: '+966', timezone: 'Asia/Riyadh', flag: '🇸🇦' },
  { name: 'Serbia', code: 'RS', dial: '+381', timezone: 'Europe/Belgrade', flag: '🇷🇸' },
  { name: 'Singapore', code: 'SG', dial: '+65', timezone: 'Asia/Singapore', flag: '🇸🇬' },
  { name: 'South Africa', code: 'ZA', dial: '+27', timezone: 'Africa/Johannesburg', flag: '🇿🇦' },
  { name: 'South Korea', code: 'KR', dial: '+82', timezone: 'Asia/Seoul', flag: '🇰🇷' },
  { name: 'Spain', code: 'ES', dial: '+34', timezone: 'Europe/Madrid', flag: '🇪🇸' },
  { name: 'Sri Lanka', code: 'LK', dial: '+94', timezone: 'Asia/Colombo', flag: '🇱🇰' },
  { name: 'Sweden', code: 'SE', dial: '+46', timezone: 'Europe/Stockholm', flag: '🇸🇪' },
  { name: 'Switzerland', code: 'CH', dial: '+41', timezone: 'Europe/Zurich', flag: '🇨🇭' },
  { name: 'Taiwan', code: 'TW', dial: '+886', timezone: 'Asia/Taipei', flag: '🇹🇼' },
  { name: 'Tanzania', code: 'TZ', dial: '+255', timezone: 'Africa/Dar_es_Salaam', flag: '🇹🇿' },
  { name: 'Thailand', code: 'TH', dial: '+66', timezone: 'Asia/Bangkok', flag: '🇹🇭' },
  { name: 'Tunisia', code: 'TN', dial: '+216', timezone: 'Africa/Tunis', flag: '🇹🇳' },
  { name: 'Turkey', code: 'TR', dial: '+90', timezone: 'Europe/Istanbul', flag: '🇹🇷' },
  { name: 'Ukraine', code: 'UA', dial: '+380', timezone: 'Europe/Kiev', flag: '🇺🇦' },
  { name: 'United Arab Emirates', code: 'AE', dial: '+971', timezone: 'Asia/Dubai', flag: '🇦🇪' },
  { name: 'United Kingdom', code: 'GB', dial: '+44', timezone: 'Europe/London', flag: '🇬🇧' },
  { name: 'United States', code: 'US', dial: '+1', timezone: 'America/New_York', flag: '🇺🇸' },
  { name: 'Venezuela', code: 'VE', dial: '+58', timezone: 'America/Caracas', flag: '🇻🇪' },
  { name: 'Vietnam', code: 'VN', dial: '+84', timezone: 'Asia/Ho_Chi_Minh', flag: '🇻🇳' },
];

const TIMEZONES: TimezoneOption[] = [
  { label: 'Auckland, New Zealand', tz: 'Pacific/Auckland' },
  { label: 'Sydney, Australia', tz: 'Australia/Sydney' },
  { label: 'Melbourne, Australia', tz: 'Australia/Melbourne' },
  { label: 'Tokyo, Japan', tz: 'Asia/Tokyo' },
  { label: 'Seoul, South Korea', tz: 'Asia/Seoul' },
  { label: 'Shanghai, China', tz: 'Asia/Shanghai' },
  { label: 'Hong Kong', tz: 'Asia/Hong_Kong' },
  { label: 'Singapore', tz: 'Asia/Singapore' },
  { label: 'Kuala Lumpur, Malaysia', tz: 'Asia/Kuala_Lumpur' },
  { label: 'Bangkok, Thailand', tz: 'Asia/Bangkok' },
  { label: 'Ho Chi Minh City, Vietnam', tz: 'Asia/Ho_Chi_Minh' },
  { label: 'Jakarta, Indonesia', tz: 'Asia/Jakarta' },
  { label: 'Manila, Philippines', tz: 'Asia/Manila' },
  { label: 'Taipei, Taiwan', tz: 'Asia/Taipei' },
  { label: 'Dhaka, Bangladesh', tz: 'Asia/Dhaka' },
  { label: 'Colombo, Sri Lanka', tz: 'Asia/Colombo' },
  { label: 'Mumbai / Delhi, India', tz: 'Asia/Kolkata' },
  { label: 'Karachi, Pakistan', tz: 'Asia/Karachi' },
  { label: 'Dubai, UAE', tz: 'Asia/Dubai' },
  { label: 'Muscat, Oman', tz: 'Asia/Muscat' },
  { label: 'Riyadh, Saudi Arabia', tz: 'Asia/Riyadh' },
  { label: 'Doha, Qatar', tz: 'Asia/Qatar' },
  { label: 'Kuwait City, Kuwait', tz: 'Asia/Kuwait' },
  { label: 'Bahrain', tz: 'Asia/Bahrain' },
  { label: 'Baghdad, Iraq', tz: 'Asia/Baghdad' },
  { label: 'Beirut, Lebanon', tz: 'Asia/Beirut' },
  { label: 'Jerusalem, Israel', tz: 'Asia/Jerusalem' },
  { label: 'Amman, Jordan', tz: 'Asia/Amman' },
  { label: 'Istanbul, Turkey', tz: 'Europe/Istanbul' },
  { label: 'Moscow, Russia', tz: 'Europe/Moscow' },
  { label: 'Nairobi, Kenya', tz: 'Africa/Nairobi' },
  { label: 'Addis Ababa, Ethiopia', tz: 'Africa/Addis_Ababa' },
  { label: 'Cairo, Egypt', tz: 'Africa/Cairo' },
  { label: 'Casablanca, Morocco', tz: 'Africa/Casablanca' },
  { label: 'Lagos, Nigeria', tz: 'Africa/Lagos' },
  { label: 'Johannesburg, South Africa', tz: 'Africa/Johannesburg' },
  { label: 'Dar es Salaam, Tanzania', tz: 'Africa/Dar_es_Salaam' },
  { label: 'Accra, Ghana', tz: 'Africa/Accra' },
  { label: 'Athens, Greece', tz: 'Europe/Athens' },
  { label: 'Bucharest, Romania', tz: 'Europe/Bucharest' },
  { label: 'Helsinki, Finland', tz: 'Europe/Helsinki' },
  { label: 'Kiev, Ukraine', tz: 'Europe/Kiev' },
  { label: 'Warsaw, Poland', tz: 'Europe/Warsaw' },
  { label: 'Berlin, Germany', tz: 'Europe/Berlin' },
  { label: 'Vienna, Austria', tz: 'Europe/Vienna' },
  { label: 'Zurich, Switzerland', tz: 'Europe/Zurich' },
  { label: 'Rome, Italy', tz: 'Europe/Rome' },
  { label: 'Paris, France', tz: 'Europe/Paris' },
  { label: 'Madrid, Spain', tz: 'Europe/Madrid' },
  { label: 'Amsterdam, Netherlands', tz: 'Europe/Amsterdam' },
  { label: 'Brussels, Belgium', tz: 'Europe/Brussels' },
  { label: 'Stockholm, Sweden', tz: 'Europe/Stockholm' },
  { label: 'Oslo, Norway', tz: 'Europe/Oslo' },
  { label: 'Copenhagen, Denmark', tz: 'Europe/Copenhagen' },
  { label: 'Dublin, Ireland', tz: 'Europe/Dublin' },
  { label: 'London, UK', tz: 'Europe/London' },
  { label: 'Lisbon, Portugal', tz: 'Europe/Lisbon' },
  { label: 'São Paulo, Brazil', tz: 'America/Sao_Paulo' },
  { label: 'Buenos Aires, Argentina', tz: 'America/Argentina/Buenos_Aires' },
  { label: 'Santiago, Chile', tz: 'America/Santiago' },
  { label: 'Lima, Peru', tz: 'America/Lima' },
  { label: 'Bogotá, Colombia', tz: 'America/Bogota' },
  { label: 'Caracas, Venezuela', tz: 'America/Caracas' },
  { label: 'Mexico City, Mexico', tz: 'America/Mexico_City' },
  { label: 'New York / Toronto, EST', tz: 'America/New_York' },
  { label: 'Chicago, CST', tz: 'America/Chicago' },
  { label: 'Denver, MST', tz: 'America/Denver' },
  { label: 'Los Angeles / Vancouver, PST', tz: 'America/Los_Angeles' },
];

// ── API helpers ────────────────────────────────────────────────────────────────

async function fetchAvailableSlots(country: string, timezone: string, signal?: AbortSignal): Promise<SlotsResponse> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/calendar-available-slots-website`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY },
    body: JSON.stringify({ country, timezone }),
    signal,
  });
  const json = await res.json();
  if (!res.ok) throw Object.assign(new Error(json.error || 'Failed to fetch slots'), { status: res.status });
  return json as SlotsResponse;
}

async function submitBooking(payload: {
  company_name?: string;
  company_website?: string;
  company_country?: string;
  dm_full_name: string;
  dm_mobile_1?: string;
  dm_email_1: string;
  start_time: string;
  sales_member_id: string;
  timezone: string;
}): Promise<BookingResult> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/book-demo-website`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to book demo');
  return json as BookingResult;
}

// ── Utilities ──────────────────────────────────────────────────────────────────

const detectTimezone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; }
};

const findTimezoneOption = (tz: string): TimezoneOption => {
  const exact = TIMEZONES.find(t => t.tz === tz);
  if (exact) return exact;
  const city = tz.split('/').pop()?.replace(/_/g, ' ') || '';
  const cityMatch = TIMEZONES.find(t => t.label.toLowerCase().includes(city.toLowerCase()));
  if (cityMatch) return cityMatch;
  return TIMEZONES.find(t => t.tz === 'America/New_York')!;
};

const formatDateLabel = (dateStr: string, tz: string): string =>
  new Intl.DateTimeFormat('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', timeZone: tz,
  }).format(new Date(dateStr + 'T12:00:00Z'));

const formatTimeInTz = (utcStr: string, tz: string): string =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz,
  }).format(new Date(utcStr));

const getTimezoneOffsetStr = (tz: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch { return ''; }
};

// ── Component ──────────────────────────────────────────────────────────────────

interface DemoBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  dialCountry: Country;
  mobileNumber: string;
  country: Country | null;
  companyName: string;
  companyWebsite: string;
}

const DEFAULT_DIAL_COUNTRY = COUNTRIES.find(c => c.code === 'US')!;

const INITIAL_FORM: FormData = {
  name: '',
  email: '',
  dialCountry: DEFAULT_DIAL_COUNTRY,
  mobileNumber: '',
  country: null,
  companyName: '',
  companyWebsite: '',
};

const DemoBookingModal: React.FC<DemoBookingModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [timezone, setTimezone] = useState<TimezoneOption>(() => findTimezoneOption(detectTimezone()));

  // Slots
  const [slotsData, setSlotsData] = useState<SlotsResponse | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  // Booking
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);
  const [confirmedLeadTz, setConfirmedLeadTz] = useState<string>('');
  const [confirmedSlotStart, setConfirmedSlotStart] = useState<string>('');

  // Dropdowns
  const [openDropdown, setOpenDropdown] = useState<'country' | 'dial' | 'timezone' | null>(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [dialSearch, setDialSearch] = useState('');
  const [timezoneSearch, setTimezoneSearch] = useState('');

  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const slotsAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const handleClick = () => setOpenDropdown(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const resetAll = () => {
    setStep(1);
    setFormData(INITIAL_FORM);
    setTimezone(findTimezoneOption(detectTimezone()));
    setSlotsData(null);
    setSlotsLoading(false);
    setSlotsError(null);
    setSelectedSlot(null);
    setIsSubmitting(false);
    setBookingResult(null);
    setConfirmedLeadTz('');
    setConfirmedSlotStart('');
    setOpenDropdown(null);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetAll();
    onClose();
  };

  const toggleDropdown = (name: 'country' | 'dial' | 'timezone', e: React.MouseEvent) => {
    e.stopPropagation();
    if (openDropdown === name) { setOpenDropdown(null); return; }
    setOpenDropdown(name);
    if (name === 'country') setCountrySearch('');
    if (name === 'dial') setDialSearch('');
    if (name === 'timezone') setTimezoneSearch('');
  };

  const selectCountry = (c: Country) => {
    setFormData(prev => ({ ...prev, country: c }));
    setOpenDropdown(null);
  };

  const selectDialCountry = (c: Country) => {
    setFormData(prev => ({ ...prev, dialCountry: c }));
    setOpenDropdown(null);
  };

  const selectTimezone = (tz: TimezoneOption) => {
    setTimezone(tz);
    setSelectedSlot(null);
    setOpenDropdown(null);
    if (formData.country) {
      loadSlots(formData.country.name, tz.tz, true);
    }
  };

  const filteredCountries = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  );
  const filteredDialCountries = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(dialSearch.toLowerCase()) || c.dial.includes(dialSearch)
  );
  const filteredTimezones = TIMEZONES.filter(t =>
    t.label.toLowerCase().includes(timezoneSearch.toLowerCase()) ||
    t.tz.toLowerCase().includes(timezoneSearch.toLowerCase())
  );

  const isStep1Valid = () =>
    formData.name.trim() &&
    formData.email.trim() &&
    formData.mobileNumber.trim() &&
    formData.country !== null &&
    formData.companyName.trim();

  const loadSlots = async (country: string, tz: string, preserveTimezone = false) => {
    slotsAbortRef.current?.abort();
    const controller = new AbortController();
    slotsAbortRef.current = controller;

    setSlotsLoading(true);
    setSlotsError(null);
    setSlotsData(null);
    setSelectedSlot(null);
    try {
      const data = await fetchAvailableSlots(country, tz, controller.signal);
      if (controller.signal.aborted) return;
      setSlotsData(data);
      if (!preserveTimezone) {
        setTimezone(findTimezoneOption(data.lead_timezone));
      }
    } catch (err: unknown) {
      if ((err as Error).name === 'AbortError') return;
      const status = (err as { status?: number }).status;
      if (status === 503) {
        setSlotsError('No availability at the moment — please try again later.');
      } else {
        setSlotsError('Something went wrong fetching available times. Please try again.');
      }
    } finally {
      if (!controller.signal.aborted) setSlotsLoading(false);
    }
  };

  const handleNextStep = async () => {
    if (!isStep1Valid() || !formData.country) return;
    setStep(2);
    await loadSlots(formData.country.name, timezone.tz);
  };

  const handleSubmit = async () => {
    if (!selectedSlot || !slotsData || !formData.country) return;
    setIsSubmitting(true);
    setSlotsError(null);
    const leadTz = timezone.tz;
    setConfirmedLeadTz(leadTz);
    setConfirmedSlotStart(selectedSlot.start);
    try {
      const result = await submitBooking({
        company_name: formData.companyName,
        company_website: formData.companyWebsite,
        company_country: formData.country.name,
        dm_full_name: formData.name,
        dm_mobile_1: `${formData.dialCountry.dial}${formData.mobileNumber}`,
        dm_email_1: formData.email,
        start_time: selectedSlot.start,
        sales_member_id: slotsData.sales_member_id,
        timezone: slotsData.timezone,
      });
      setBookingResult(result);
      trackDemoBooked({
        name: formData.name,
        email: formData.email,
        phone: `${formData.dialCountry.dial}${formData.mobileNumber.replace(/\D/g, '')}`,
      });
    } catch {
      setSlotsError('Failed to book the demo. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full px-3 py-3 bg-chip-bg border border-chip-border rounded-lg text-text-primary placeholder-text-secondary focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-colors text-sm demo-input outline-none';
  const labelClass = 'block text-xs font-semibold text-text-primary uppercase tracking-wide mb-1.5';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div
        className="relative bg-bg-surface border border-card-border rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden shadow-glow-combined flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 to-brand-green-teal/5 rounded-2xl pointer-events-none" />

        <div className="relative z-10 flex flex-col min-h-0">
          {/* Header */}
          <div className="px-6 py-5 border-b border-card-border flex-shrink-0">
            <div className="flex items-center justify-between gap-3">
              {/* Left: title + subtitle */}
              <div>
                <h3 className="text-xl font-bold text-text-primary">Book Your Live Demo</h3>
                <p className="text-text-secondary text-sm mt-0.5">See your AI Sales Team in action</p>
              </div>
              {/* Right: step indicator (desktop) + close button */}
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="hidden sm:flex items-center gap-2 text-xs font-medium">
                  <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold transition-colors ${step === 1 ? 'bg-brand-blue text-white' : 'bg-brand-green-teal text-white'}`}>
                    {step === 1 ? '1' : <Check size={12} />}
                  </span>
                  <span className={`transition-colors ${step === 1 ? 'text-text-primary' : 'text-text-secondary'}`}>Info</span>
                  <div className={`w-6 h-px transition-colors ${step === 2 ? 'bg-brand-blue' : 'bg-card-border'}`} />
                  <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold transition-colors ${step === 2 ? 'bg-brand-blue text-white' : 'bg-chip-bg border border-chip-border text-text-secondary'}`}>
                    2
                  </span>
                  <span className={`transition-colors ${step === 2 ? 'text-text-primary' : 'text-text-secondary'}`}>Time</span>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
                >
                  <X size={22} />
                </button>
              </div>
            </div>
            {/* Step indicator — mobile only */}
            <div className="flex sm:hidden items-center gap-2 text-xs font-medium mt-3">
              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold transition-colors ${step === 1 ? 'bg-brand-blue text-white' : 'bg-brand-green-teal text-white'}`}>
                {step === 1 ? '1' : <Check size={12} />}
              </span>
              <span className={`transition-colors ${step === 1 ? 'text-text-primary' : 'text-text-secondary'}`}>Info</span>
              <div className={`w-6 h-px transition-colors ${step === 2 ? 'bg-brand-blue' : 'bg-card-border'}`} />
              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold transition-colors ${step === 2 ? 'bg-brand-blue text-white' : 'bg-chip-bg border border-chip-border text-text-secondary'}`}>
                2
              </span>
              <span className={`transition-colors ${step === 2 ? 'text-text-primary' : 'text-text-secondary'}`}>Time</span>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 px-6 py-5 modal-dark-scrollbar" ref={dropdownContainerRef}>

            {/* ── Success ── */}
            {bookingResult ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-brand-green-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-brand-green-teal" />
                </div>
                <h4 className="text-2xl font-bold text-text-primary mb-2">Demo Booked!</h4>
                <p className="text-text-secondary mb-1">
                  Scheduled for{' '}
                  <span className="text-text-primary font-semibold">
                    {new Intl.DateTimeFormat('en-US', {
                      weekday: 'long', month: 'long', day: 'numeric',
                      timeZone: confirmedLeadTz,
                    }).format(new Date(confirmedSlotStart))}
                    {' '}at{' '}
                    {new Intl.DateTimeFormat('en-US', {
                      hour: 'numeric', minute: '2-digit', hour12: true,
                      timeZone: confirmedLeadTz,
                    }).format(new Date(confirmedSlotStart))}
                  </span>
                </p>
                <p className="text-text-secondary text-sm mb-6">
                  {getTimezoneOffsetStr(confirmedLeadTz)} &middot; {confirmedLeadTz}
                </p>
                {bookingResult.meeting_link && (
                  <a
                    href={bookingResult.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-blue/15 border border-brand-blue/30 text-brand-blue font-semibold text-sm hover:bg-brand-blue/25 transition-colors mb-5"
                  >
                    <ExternalLink size={15} />
                    Join Meeting Link
                  </a>
                )}
                <p className="text-text-secondary text-sm mb-8">
                  A calendar invite with all details has been sent to{' '}
                  <span className="text-text-primary">{formData.email}</span>.
                </p>
                <button
                  onClick={handleClose}
                  className="px-8 py-3 rounded-xl bg-gradient-cta text-cta-text font-bold transition-all duration-300 hover:shadow-xl hover:scale-105"
                >
                  Close
                </button>
              </div>

            ) : step === 1 ? (
              /* ── Step 1: Info ── */
              <div className="space-y-5">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-brand-green-teal" />
                  <h4 className="text-base font-bold text-text-primary">Your Information</h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Full Name */}
                  <div>
                    <label className={labelClass}>Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={14} />
                      <input type="text" value={formData.name}
                        onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                        className={`${inputClass} pl-9`} placeholder="John Smith" />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className={labelClass}>Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={14} />
                      <input type="email" value={formData.email}
                        onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                        className={`${inputClass} pl-9`} placeholder="john@company.com" />
                    </div>
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className={labelClass}>Mobile Number *</label>
                    <div className="flex gap-0 rounded-lg border border-chip-border focus-within:ring-2 focus-within:ring-brand-blue focus-within:border-transparent">
                      <div className="relative flex-shrink-0" style={{ width: '100px' }}>
                        <button type="button" onClick={e => toggleDropdown('dial', e)}
                          className="w-full h-full flex items-center gap-1 px-2.5 bg-chip-bg hover:bg-chip-border transition-colors border-r border-chip-border rounded-l-lg text-text-primary text-sm font-medium">
                          <FlagImg flag={formData.dialCountry.flag} />
                          <span className="text-xs text-text-secondary">{formData.dialCountry.dial}</span>
                          <ChevronDown size={12} className="text-text-secondary ml-auto flex-shrink-0" />
                        </button>
                        {openDropdown === 'dial' && (
                          <div className="absolute top-full left-0 mt-1 w-52 bg-bg-surface border border-card-border rounded-xl shadow-2xl z-50 overflow-hidden"
                            onClick={e => e.stopPropagation()}>
                            <div className="p-2 border-b border-card-border">
                              <input autoFocus type="text" value={dialSearch}
                                onChange={e => setDialSearch(e.target.value)}
                                className="w-full px-3 py-2 bg-chip-bg border border-chip-border rounded-lg text-text-primary placeholder-text-secondary text-sm outline-none focus:ring-1 focus:ring-brand-blue"
                                placeholder="Search country or code..."
                                onClick={e => e.stopPropagation()} />
                            </div>
                            <div className="overflow-y-auto max-h-48 modal-dark-scrollbar">
                              {filteredDialCountries.map(c => (
                                <button key={c.code} type="button" onClick={() => selectDialCountry(c)}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-chip-bg text-left transition-colors">
                                  <FlagImg flag={c.flag} />
                                  <span className="text-text-primary text-sm flex-1 truncate">{c.name}</span>
                                  <span className="text-text-secondary text-xs flex-shrink-0">{c.dial}</span>
                                </button>
                              ))}
                              {filteredDialCountries.length === 0 && (
                                <p className="text-text-secondary text-sm text-center py-4">No results</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <input type="tel" value={formData.mobileNumber}
                        onChange={e => setFormData(p => ({ ...p, mobileNumber: e.target.value }))}
                        className="flex-1 px-3 py-3 bg-chip-bg text-text-primary placeholder-text-secondary text-sm outline-none rounded-r-lg"
                        placeholder="555 123 4567" />
                    </div>
                  </div>

                  {/* Country */}
                  <div>
                    <label className={labelClass}>Country *</label>
                    <div className="relative">
                      <button type="button" onClick={e => toggleDropdown('country', e)}
                        className={`w-full flex items-center gap-2 px-3 py-3 bg-chip-bg border border-chip-border rounded-lg text-sm transition-colors hover:border-brand-blue/50 ${openDropdown === 'country' ? 'ring-2 ring-brand-blue border-transparent' : ''}`}>
                        {formData.country ? (
                          <>
                            <FlagImg flag={formData.country.flag} />
                            <span className="text-text-primary flex-1 text-left">{formData.country.name}</span>
                          </>
                        ) : (
                          <>
                            <MapPin size={14} className="text-text-secondary flex-shrink-0" />
                            <span className="text-text-secondary flex-1 text-left">Select country</span>
                          </>
                        )}
                        <ChevronDown size={14} className="text-text-secondary flex-shrink-0" />
                      </button>
                      {openDropdown === 'country' && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-bg-surface border border-card-border rounded-xl shadow-2xl z-50 overflow-hidden"
                          onClick={e => e.stopPropagation()}>
                          <div className="p-2 border-b border-card-border">
                            <input autoFocus type="text" value={countrySearch}
                              onChange={e => setCountrySearch(e.target.value)}
                              className="w-full px-3 py-2 bg-chip-bg border border-chip-border rounded-lg text-text-primary placeholder-text-secondary text-sm outline-none focus:ring-1 focus:ring-brand-blue"
                              placeholder="Search country..."
                              onClick={e => e.stopPropagation()} />
                          </div>
                          <div className="overflow-y-auto max-h-48 modal-dark-scrollbar">
                            {filteredCountries.map(c => (
                              <button key={c.code} type="button" onClick={() => selectCountry(c)}
                                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-chip-bg text-left transition-colors">
                                <FlagImg flag={c.flag} />
                                <span className="text-text-primary text-sm flex-1">{c.name}</span>
                                {formData.country?.code === c.code && (
                                  <Check size={14} className="text-brand-blue ml-auto" />
                                )}
                              </button>
                            ))}
                            {filteredCountries.length === 0 && (
                              <p className="text-text-secondary text-sm text-center py-4">No results</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className={labelClass}>Company Name *</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={14} />
                      <input type="text" value={formData.companyName}
                        onChange={e => setFormData(p => ({ ...p, companyName: e.target.value }))}
                        className={`${inputClass} pl-9`} placeholder="TechCorp Solutions" />
                    </div>
                  </div>

                  {/* Company Website */}
                  <div>
                    <label className={labelClass}>Company Website</label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={14} />
                      <input type="text" value={formData.companyWebsite}
                        onChange={e => setFormData(p => ({ ...p, companyWebsite: e.target.value }))}
                        className={`${inputClass} pl-9`} placeholder="https://techcorp.com" />
                    </div>
                  </div>
                </div>

                <button type="button" onClick={handleNextStep} disabled={!isStep1Valid()}
                  className="w-full mt-2 px-6 py-4 rounded-xl bg-gradient-cta text-cta-text font-bold text-base transition-all duration-300 hover:shadow-xl hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none cta-hover-overlay flex items-center justify-center gap-3">
                  <span>Next — Select Date &amp; Time</span>
                  <ArrowRight size={18} />
                </button>
              </div>

            ) : (
              /* ── Step 2: Pick slot ── */
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => { setStep(1); setSlotsError(null); }}
                    className="flex items-center gap-1.5 text-text-secondary hover:text-text-primary text-sm font-medium transition-colors">
                    <ArrowLeft size={16} />
                    Back
                  </button>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-brand-blue" />
                    <h4 className="text-base font-bold text-text-primary">Select Your Preferred Time</h4>
                  </div>
                </div>

                {/* Timezone selector */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Clock size={14} className="text-text-secondary flex-shrink-0" />
                  <span className="text-text-secondary text-sm">Timezone:</span>
                  <div className="relative">
                    <button type="button" onClick={e => toggleDropdown('timezone', e)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-chip-bg border border-chip-border rounded-lg text-sm text-text-primary hover:border-brand-blue/50 transition-colors">
                      <span className="font-medium">{timezone.label}</span>
                      <span className="text-text-secondary">({getTimezoneOffsetStr(timezone.tz)})</span>
                      <ChevronDown size={13} className="text-text-secondary" />
                    </button>
                    {openDropdown === 'timezone' && (
                      <div className="absolute top-full left-0 mt-1 w-72 bg-bg-surface border border-card-border rounded-xl shadow-2xl z-50 overflow-hidden"
                        onClick={e => e.stopPropagation()}>
                        <div className="p-2 border-b border-card-border">
                          <input autoFocus type="text" value={timezoneSearch}
                            onChange={e => setTimezoneSearch(e.target.value)}
                            className="w-full px-3 py-2 bg-chip-bg border border-chip-border rounded-lg text-text-primary placeholder-text-secondary text-sm outline-none focus:ring-1 focus:ring-brand-blue"
                            placeholder="Search timezone or city..."
                            onClick={e => e.stopPropagation()} />
                        </div>
                        <div className="overflow-y-auto max-h-56 modal-dark-scrollbar">
                          {filteredTimezones.map(t => (
                            <button key={t.tz} type="button" onClick={() => selectTimezone(t)}
                              className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-chip-bg text-left transition-colors">
                              <span className="text-text-primary text-sm">{t.label}</span>
                              <span className="text-text-secondary text-xs ml-2 flex-shrink-0">{getTimezoneOffsetStr(t.tz)}</span>
                            </button>
                          ))}
                          {filteredTimezones.length === 0 && (
                            <p className="text-text-secondary text-sm text-center py-4">No results</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Slot grid / loading / error */}
                {slotsLoading ? (
                  <div className="flex flex-col items-center justify-center py-14 gap-3">
                    <Loader2 className="w-8 h-8 text-brand-blue animate-spin" />
                    <p className="text-text-secondary text-sm">Finding available times...</p>
                  </div>
                ) : slotsError ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-4">
                    <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-red-400" />
                    </div>
                    <p className="text-text-secondary text-sm text-center max-w-xs">{slotsError}</p>
                    <button type="button"
                      onClick={() => formData.country && loadSlots(formData.country.name, timezone.tz)}
                      className="px-5 py-2 rounded-lg bg-chip-bg border border-chip-border text-text-primary text-sm font-medium hover:border-brand-blue/40 transition-colors">
                      Try Again
                    </button>
                  </div>
                ) : slotsData ? (
                  slotsData.days.length === 0 || slotsData.days.every(d => d.slots.length === 0) ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-4">
                      <div className="w-12 h-12 bg-brand-blue/10 rounded-full flex items-center justify-center">
                        <CalendarX className="w-6 h-6 text-brand-blue" />
                      </div>
                      <div className="text-center">
                        <p className="text-text-primary font-semibold text-sm mb-1">No Available Times Right Now</p>
                        <p className="text-text-secondary text-sm max-w-xs">Our team has no open slots at the moment. Please check back soon or reach us directly.</p>
                      </div>
                      <button type="button"
                        onClick={() => formData.country && loadSlots(formData.country.name, timezone.tz)}
                        className="px-5 py-2 rounded-lg bg-chip-bg border border-chip-border text-text-primary text-sm font-medium hover:border-brand-blue/40 transition-colors">
                        Refresh
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {slotsData.days.map(day => (
                        <div key={day.date}>
                          <h5 className="text-text-primary font-semibold text-sm mb-2.5">
                            {formatDateLabel(day.date, timezone.tz)}
                          </h5>
                          <div className="space-y-2">
                            {day.slots.map(slot => {
                              const isSelected = selectedSlot?.start === slot.start;
                              return (
                                <button key={slot.start} type="button"
                                  onClick={() => setSelectedSlot(slot)}
                                  className={`w-full p-2.5 rounded-lg border-2 font-medium transition-all text-left flex items-center gap-2.5 text-sm ${
                                    isSelected
                                      ? 'border-brand-blue bg-brand-blue/10 text-brand-blue'
                                      : 'border-chip-border bg-chip-bg text-chip-text hover:border-brand-blue/40 hover:bg-brand-blue/5'
                                  }`}>
                                  <Clock size={13} className={isSelected ? 'text-brand-blue' : 'text-text-secondary'} />
                                  <span>{formatTimeInTz(slot.start, timezone.tz)}</span>
                                  {isSelected && <Check size={13} className="ml-auto" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : null}

                {/* Book button */}
                <button type="button" onClick={handleSubmit}
                  disabled={!selectedSlot || isSubmitting || slotsLoading}
                  className="w-full mt-1 px-6 py-4 rounded-xl bg-gradient-cta text-cta-text font-bold text-base transition-all duration-300 hover:shadow-xl hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none cta-hover-overlay flex items-center justify-center gap-3">
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Calendar size={18} />
                      <span>Book Your Demo Now</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoBookingModal;
